import { useEffect, useState } from "react";
import { toast, Toaster } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Save, Lock } from "lucide-react";

const AdminSettings = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pwd, setPwd] = useState({ current_password: "", new_password: "" });

  const load = async () => {
    try {
      const { data } = await api.get("/admin/settings");
      setData(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { load(); }, []);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const setSection = (k) => setData((d) => ({ ...d, section_visibility: { ...d.section_visibility, [k]: !d.section_visibility[k] } }));

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...data };
      delete payload.updated_at;
      await api.put("/admin/settings", payload);
      toast.success("تم حفظ الإعدادات");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    try {
      await api.post("/auth/change-password", pwd);
      setPwd({ current_password: "", new_password: "" });
      toast.success("تم تغيير كلمة المرور");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  if (!data) return <div className="overline animate-pulse">…</div>;

  return (
    <div data-testid="admin-settings">
      <Toaster position="top-left" theme="dark" richColors />
      <div className="overline mb-3">الإعدادات</div>
      <h1 className="font-arabic font-black text-3xl sm:text-4xl text-bone mb-10">إعدادات الموقع</h1>

      <form onSubmit={onSave} className="space-y-10 max-w-3xl">
        <Section title="الهوية">
          <Input label="اسم الموقع (عربي)" value={data.site_name_ar} onChange={(v) => set("site_name_ar", v)} />
          <Input label="اسم الموقع (إنجليزي)" value={data.site_name_en} onChange={(v) => set("site_name_en", v)} ltr />
          <Textarea label="وصف الموقع (عربي)" value={data.site_description_ar} onChange={(v) => set("site_description_ar", v)} />
          <Textarea label="وصف الموقع (إنجليزي)" value={data.site_description_en} onChange={(v) => set("site_description_en", v)} />
        </Section>

        <Section title="اللغة">
          <label className="block">
            <span className="block mb-2 text-xs tracking-[0.2em] uppercase text-white/50 font-arabic">اللغة الافتراضية</span>
            <select value={data.default_language} onChange={(e) => set("default_language", e.target.value)} className="bg-ink-soft border border-white/15 px-4 py-3 w-full">
              <option value="ar">العربية</option>
              <option value="en">English</option>
            </select>
          </label>
          <label className="flex items-center gap-3 text-sm font-arabic">
            <input type="checkbox" checked={!!data.english_enabled} onChange={(e) => set("english_enabled", e.target.checked)} className="accent-gold" />
            تفعيل اللغة الإنجليزية في الموقع
          </label>
        </Section>

        <Section title="التواصل و SEO">
          <Input label="البريد الرسمي" value={data.contact_email} onChange={(v) => set("contact_email", v)} ltr />
          <Textarea label="كلمات SEO المفتاحية (مفصولة بفواصل)" value={data.seo_keywords} onChange={(v) => set("seo_keywords", v)} />
        </Section>

        <Section title="ظهور الأقسام">
          <div className="grid grid-cols-2 gap-3">
            {Object.entries(data.section_visibility || {}).map(([k, v]) => (
              <label key={k} className="flex items-center gap-3 font-arabic text-sm border border-white/10 px-4 py-3">
                <input type="checkbox" checked={!!v} onChange={() => setSection(k)} className="accent-gold" />
                {labelFor(k)}
              </label>
            ))}
          </div>
        </Section>

        <button type="submit" disabled={saving} className="btn-gold disabled:opacity-60" data-testid="settings-save"><Save size={14} />{saving ? "جاري الحفظ…" : "حفظ الإعدادات"}</button>
      </form>

      <div className="border-t border-white/10 mt-16 pt-12 max-w-md">
        <div className="overline mb-3">الأمان</div>
        <h2 className="font-arabic font-bold text-xl text-bone mb-6 flex items-center gap-3"><Lock size={16} />تغيير كلمة المرور — {user?.email}</h2>
        <form onSubmit={changePassword} className="space-y-5" data-testid="change-password-form">
          <Input label="كلمة المرور الحالية" type="password" value={pwd.current_password} onChange={(v) => setPwd((p) => ({ ...p, current_password: v }))} ltr />
          <Input label="كلمة المرور الجديدة (٨+)" type="password" value={pwd.new_password} onChange={(v) => setPwd((p) => ({ ...p, new_password: v }))} ltr />
          <button type="submit" className="btn-gold" data-testid="change-password-submit">تحديث كلمة المرور</button>
        </form>
      </div>
    </div>
  );
};

const Section = ({ title, children }) => (
  <fieldset className="border border-white/10 bg-ink-soft p-6 space-y-5">
    <legend className="overline px-3">{title}</legend>
    {children}
  </fieldset>
);

const Input = ({ label, value, onChange, type = "text", ltr }) => (
  <label className="block">
    <span className="block mb-2 text-xs tracking-[0.2em] uppercase text-white/50 font-arabic">{label}</span>
    <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} className="editorial-input" dir={ltr ? "ltr" : "auto"} />
  </label>
);

const Textarea = ({ label, value, onChange }) => (
  <label className="block">
    <span className="block mb-2 text-xs tracking-[0.2em] uppercase text-white/50 font-arabic">{label}</span>
    <textarea rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)} className="editorial-input resize-none" />
  </label>
);

const labelFor = (k) => ({
  about: "نبذة",
  media: "الجهات الإعلامية",
  timeline: "المشاريع",
  articles: "المقالات",
  contact: "التواصل",
}[k] || k);

export default AdminSettings;
